import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Input,
  Modal,
  Form,
  message,
  Popconfirm,
  Tag,
  Space,
  Divider,
  List,
  Switch,
} from "antd";
import { SearchOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "@/ag-grid-overrides.css";
import api, {
  createCategory,
  createSubcategory,
  deleteCategory,
  deleteSubcategory,
  getCategories,
  getSubcategories,
  toggleCategoryStatus,
  toggleSubcategoryStatus,
} from "@/services/api";
import { useTheme } from "@/contexts/ThemeContext";

const Categories = () => {
  const { isDark } = useTheme();
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [subcategoryModalVisible, setSubcategoryModalVisible] = useState(false);
  const [subcategoryCategory, setSubcategoryCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [savingSubcategory, setSavingSubcategory] = useState(false);
  const [form] = Form.useForm();
  const [subcategoryForm] = Form.useForm();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, isError } = await getCategories();
      if (isError || data?.statuscode !== 1) {
        return message.error(data?.message || "Failed to fetch categories");
      }
      setCategories(data?.data || []);
    } catch (error) {
      message.error("Failed to fetch categories");
    }
  };

  const handleToggleCategory = async (category) => {
    try {
      await toggleCategoryStatus(category.uid);
      message.success(
        category.active
          ? "Category disabled successfully"
          : "Category enabled successfully",
      );
      fetchCategories();
    } catch (error) {
      message.error("Failed to update category status");
    }
  };

  const handleDeleteCategory = async (category) => {
    try {
      await deleteCategory(category.id);
      message.success("Category deleted successfully");
      fetchCategories();
    } catch (error) {
      message.error("Failed to delete category");
    }
  };

  const fetchSubcategories = async (categoryUid) => {
    if (!categoryUid) return;
    setSubcategoriesLoading(true);
    try {
      const { data, isError } = await getSubcategories(categoryUid);
      if (isError || data?.statuscode === 0) {
        throw new Error(data?.message || "Failed to fetch subcategories");
      }
      setSubcategories(data?.data || []);
    } catch (error) {
      message.error("Failed to fetch subcategories");
      setSubcategories([]);
    } finally {
      setSubcategoriesLoading(false);
    }
  };

  const openSubcategoriesModal = async (category) => {
    setSubcategoryCategory(category);
    setSubcategoryModalVisible(true);
    subcategoryForm.resetFields();
    await fetchSubcategories(category?.uid);
  };

  const closeSubcategoriesModal = () => {
    setSubcategoryModalVisible(false);
    setSubcategoryCategory(null);
    setSubcategories([]);
    subcategoryForm.resetFields();
  };

  const handleCreateSubcategory = async (values) => {
    if (!subcategoryCategory?.uid) return;

    setSavingSubcategory(true);
    try {
      await createSubcategory(subcategoryCategory.uid, {
        name: values?.name?.trim(),
      });
      message.success("Subcategory created successfully");
      subcategoryForm.resetFields();
      fetchSubcategories(subcategoryCategory.uid);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to create subcategory",
      );
    } finally {
      setSavingSubcategory(false);
    }
  };

  const handleToggleSubcategory = async (subcategory) => {
    try {
      await toggleSubcategoryStatus(subcategory.uid);
      message.success("Subcategory status updated");
      fetchSubcategories(subcategoryCategory?.uid);
    } catch (error) {
      message.error("Failed to update subcategory status");
    }
  };

  const handleDeleteSubcategory = async (subcategory) => {
    try {
      await deleteSubcategory(subcategory.uid);
      message.success("Subcategory deleted successfully");
      fetchSubcategories(subcategoryCategory?.uid);
    } catch (error) {
      message.error("Failed to delete subcategory");
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, values);
        message.success("Category updated successfully");
      } else {
        await createCategory({
          name: values?.name,
          color: values?.color,
        });
        message.success("Category created successfully");
      }
      setModalVisible(false);
      form.resetFields();
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      message.error("Failed to save category");
    }
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      name: category.name,
      color: category.color,
    });
    setModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    form.setFieldsValue({ name: "", color: "" });
    setModalVisible(true);
  };

  const columnDefs = [
    { field: "id", headerName: "ID", width: 70, filter: true },
    { field: "uid", headerName: "UID", minWidth: 260, flex: 1, filter: true },
    { field: "name", headerName: "Name", filter: true, minWidth: 180 },
    {
      field: "color",
      headerName: "Color Class",
      minWidth: 220,
      flex: 1,
      filter: true,
    },
    {
      field: "active",
      headerName: "Status",
      width: 120,
      cellRenderer: (params) => (
        <Tag
          color={params.value ? "success" : "default"}
          style={{
            borderRadius: 0,
            fontWeight: 500,
            fontSize: 11,
            textTransform: "uppercase",
          }}
        >
          {params.value ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      field: "createdon",
      headerName: "Created",
      minWidth: 180,
      valueFormatter: ({ value }) =>
        value ? new Date(value).toLocaleString() : "-",
    },
    {
      field: "updatedon",
      headerName: "Updated",
      minWidth: 180,
      valueFormatter: ({ value }) =>
        value ? new Date(value).toLocaleString() : "-",
    },
    {
      headerName: "Actions",
      width: 420,
      cellRenderer: (params) => (
        <Space>
          <Button
            size="small"
            style={{ borderRadius: 0, fontWeight: 500 }}
            onClick={() => openEditModal(params.data)}
            data-testid={`edit-category-${params.data.id}`}
          >
            Edit
          </Button>
          <Switch
            checked={Boolean(params.data.active)}
            onChange={() => handleToggleCategory(params.data)}
            checkedChildren="On"
            unCheckedChildren="Off"
          />
          <Button
            size="small"
            style={{ borderRadius: 0, fontWeight: 500 }}
            onClick={() => handleToggleCategory(params.data)}
            data-testid={`toggle-category-${params.data.id}`}
          >
            {params.data.active ? "Disable" : "Enable"}
          </Button>
          <Button
            size="small"
            style={{ borderRadius: 0, fontWeight: 500 }}
            onClick={() => openSubcategoriesModal(params.data)}
            data-testid={`manage-subcategories-${params.data.id}`}
          >
            Subcategories
          </Button>
          <Popconfirm
            title="Delete Category"
            description={`Delete "${params.data.name}"?`}
            onConfirm={() => handleDeleteCategory(params.data)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button
              size="small"
              danger
              style={{ borderRadius: 0, fontWeight: 500 }}
              data-testid={`delete-category-${params.data.id}`}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredCategories = categories.filter((category) =>
    Object.values(category).some((val) =>
      String(val).toLowerCase().includes(searchText.toLowerCase()),
    ),
  );

  return (
    <div>
      <div
        style={{
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 28,
              fontWeight: 700,
              fontFamily: "IBM Plex Sans, sans-serif",
              letterSpacing: "-0.02em",
              marginBottom: 8,
            }}
          >
            Categories Management
          </h1>
          <p
            style={{
              fontSize: 14,
              color: isDark ? "#A1A1AA" : "#71717A",
              margin: 0,
            }}
          >
            Manage content categories and taxonomy
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
          style={{ borderRadius: 0, fontWeight: 500, height: 40 }}
          data-testid="create-category-button"
        >
          Add Category
        </Button>
      </div>

      <Card
        style={{
          borderRadius: 0,
          border: `1px solid ${isDark ? "#27272A" : "#E4E4E7"}`,
          background: isDark ? "#18181B" : "#FFFFFF",
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search categories..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 400, borderRadius: 0 }}
            data-testid="categories-search-input"
          />
        </div>
        <div
          className={isDark ? "ag-theme-alpine dark" : "ag-theme-alpine"}
          style={{ height: 600, width: "100%" }}
        >
          <AgGridReact
            rowData={filteredCategories}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={20}
            domLayout="normal"
          />
        </div>
      </Card>

      <Modal
        title={editingCategory ? "Edit Category" : "Create Category"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingCategory(null);
        }}
        footer={null}
        styles={{ borderRadius: 0 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: 24 }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter category name" }]}
          >
            <Input
              style={{ borderRadius: 0 }}
              data-testid="category-name-input"
            />
          </Form.Item>
          <Form.Item
            name="color"
            label="Color Class"
            rules={[{ required: true, message: "Please enter color class" }]}
          >
            <Input
              placeholder="bg-cyan-600 hover:bg-cyan-700"
              style={{ borderRadius: 0 }}
              data-testid="category-color-input"
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                style={{ borderRadius: 0 }}
                data-testid="category-submit-button"
              >
                {editingCategory ? "Update" : "Create"}
              </Button>
              <Button
                onClick={() => setModalVisible(false)}
                style={{ borderRadius: 0 }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          subcategoryCategory
            ? `Subcategories - ${subcategoryCategory.name}`
            : "Subcategories"
        }
        open={subcategoryModalVisible}
        onCancel={closeSubcategoriesModal}
        footer={null}
        width={720}
      >
        <Form
          form={subcategoryForm}
          layout="inline"
          onFinish={handleCreateSubcategory}
          style={{ marginBottom: 20, gap: 12 }}
        >
          <Form.Item
            name="name"
            rules={[
              { required: true, message: "Please enter subcategory name" },
            ]}
            style={{ flex: 1, minWidth: 260, marginBottom: 0 }}
          >
            <Input
              placeholder="Enter subcategory name"
              style={{ borderRadius: 0, width: "100%" }}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={savingSubcategory}
              icon={<PlusOutlined />}
              style={{ borderRadius: 0 }}
            >
              Add Subcategory
            </Button>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchSubcategories(subcategoryCategory?.uid)}
              style={{ borderRadius: 0 }}
            >
              Refresh
            </Button>
          </Form.Item>
        </Form>

        <Divider style={{ margin: "16px 0" }} />

        <List
          loading={subcategoriesLoading}
          dataSource={subcategories}
          locale={{ emptyText: "No subcategories yet." }}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Switch
                  key="toggle"
                  checked={Boolean(item.active)}
                  onChange={() => handleToggleSubcategory(item)}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                />,
                <Popconfirm
                  key="delete"
                  title="Delete Subcategory"
                  description={`Delete "${item.name}"?`}
                  okText="Delete"
                  cancelText="Cancel"
                  onConfirm={() => handleDeleteSubcategory(item)}
                >
                  <Button danger style={{ borderRadius: 0 }}>
                    Delete
                  </Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={item.name}
                description={
                  <Tag
                    color={item.active ? "green" : "default"}
                    style={{ borderRadius: 0, marginTop: 4 }}
                  >
                    {item.active ? "Active" : "Inactive"}
                  </Tag>
                }
              />
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
};

export default Categories;
